import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: "Curso de Sangrador de Seringueira (SENAR)",
  city: "", uf: "SP", start_date: "", end_date: "",
  horario: "8h às 17h", carga_horaria: "24 horas (3 dias)",
  formato: "Teoria + prática na seringueira",
  conteudo: "Saúde e segurança, escolha da árvore, abertura de painel, sistemas de sangria, consumo de casca, uso de estimulante, horário e número de plantas, coleta e comercialização",
  prerequisito: "Maior de 18 anos", valor: "Gratuito", certificado: "Sim — certificado SENAR",
  vagas: 16, local: "", contact_name: "", contact_phone: "", extra_info: "",
};

const shareText = (c) =>
  `🎓 CURSO GRATUITO DE SANGRIA DE SERINGUEIRA\n` +
  `📍 ${c.city}${c.uf ? `/${c.uf}` : ""} — ${c.dates_label}\n` +
  `⏱ ${c.carga_horaria} · ${c.certificado}\n` +
  (c.local ? `📌 Local: ${c.local}\n` : "") +
  `👥 Vagas limitadas! Inscrições: ${c.contact_name || "Sindicato"}${c.contact_phone ? ` 📱 ${c.contact_phone}` : ""}\n\n` +
  `Depois do curso, cadastre-se grátis no Balcão de Empregos e seja encontrado pelas fazendas:\n` +
  `👉 ${c.tracked_link}`;

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs font-semibold text-gray-600">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full border rounded-lg px-3 py-2 text-sm mt-1";

const CursoForm = ({ initial, onSaved, onCancel, editId }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.city || !form.start_date || !form.end_date) {
      toast.error("Preencha cidade e as datas de início e fim");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, vagas: form.vagas ? parseInt(form.vagas) : null };
      if (editId) await axios.put(`${API}/admin/cursos/${editId}`, payload);
      else await axios.post(`${API}/admin/cursos`, payload);
      toast.success(editId ? "Curso atualizado!" : "Curso publicado na página /cursos!");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar curso");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-green-300 bg-green-50/50 rounded-xl p-4 space-y-3" data-testid="curso-form">
      <p className="text-xs text-green-800 bg-green-100 rounded-lg px-3 py-2">
        ✨ Modelo padrão SENAR já preenchido — normalmente você só muda <b>cidade, datas, local e contato</b>.
      </p>
      <div className="grid grid-cols-3 gap-2">
        <div className="col-span-2"><Field label="Cidade *"><input className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Votuporanga" data-testid="curso-form-city" /></Field></div>
        <Field label="UF"><input className={inputCls} maxLength={2} value={form.uf} onChange={(e) => set("uf", e.target.value.toUpperCase())} data-testid="curso-form-uf" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Início *"><input type="date" className={inputCls} value={form.start_date} onChange={(e) => set("start_date", e.target.value)} data-testid="curso-form-start" /></Field>
        <Field label="Fim *"><input type="date" className={inputCls} value={form.end_date} onChange={(e) => set("end_date", e.target.value)} data-testid="curso-form-end" /></Field>
      </div>
      <Field label="Local"><input className={inputCls} value={form.local} onChange={(e) => set("local", e.target.value)} placeholder="Sindicato Rural de Votuporanga" data-testid="curso-form-local" /></Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Responsável (inscrições)"><input className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Solange" data-testid="curso-form-contact-name" /></Field>
        <Field label="WhatsApp do responsável"><input className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="(17) 99709-4394" data-testid="curso-form-contact-phone" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Nº de vagas"><input type="number" className={inputCls} value={form.vagas ?? ""} onChange={(e) => set("vagas", e.target.value)} data-testid="curso-form-vagas" /></Field>
        <Field label="Horário"><input className={inputCls} value={form.horario} onChange={(e) => set("horario", e.target.value)} /></Field>
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-gray-600 font-semibold">Campos do modelo SENAR (clique para editar)</summary>
        <div className="space-y-2 mt-2">
          <Field label="Título"><input className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
          <Field label="Carga horária"><input className={inputCls} value={form.carga_horaria} onChange={(e) => set("carga_horaria", e.target.value)} /></Field>
          <Field label="Formato"><input className={inputCls} value={form.formato} onChange={(e) => set("formato", e.target.value)} /></Field>
          <Field label="Conteúdo"><textarea className={inputCls} rows={3} value={form.conteudo} onChange={(e) => set("conteudo", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Pré-requisito"><input className={inputCls} value={form.prerequisito} onChange={(e) => set("prerequisito", e.target.value)} /></Field>
            <Field label="Valor"><input className={inputCls} value={form.valor} onChange={(e) => set("valor", e.target.value)} /></Field>
          </div>
          <Field label="Certificado"><input className={inputCls} value={form.certificado} onChange={(e) => set("certificado", e.target.value)} /></Field>
          <Field label="Informações extras"><input className={inputCls} value={form.extra_info} onChange={(e) => set("extra_info", e.target.value)} placeholder="Ex: levar protetor solar e marmita" /></Field>
        </div>
      </details>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800" data-testid="curso-form-save">
          {saving ? "Salvando…" : editId ? "💾 Salvar alterações" : "✅ Publicar curso"}
        </Button>
        <Button variant="outline" onClick={onCancel} data-testid="curso-form-cancel">Cancelar</Button>
      </div>
    </div>
  );
};

const AlunosList = ({ courseId }) => {
  const [alunos, setAlunos] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API}/admin/cursos/${courseId}/alunos`).then((r) => setAlunos(r.data.alunos)).catch(() => setAlunos([]));
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (userId) => {
    try {
      const r = await axios.post(`${API}/admin/cursos/${courseId}/confirm/${userId}`);
      toast.success(r.data.confirmed ? "✅ Aluno confirmado como formado!" : "Confirmação removida");
      load();
    } catch {
      toast.error("Erro ao confirmar aluno");
    }
  };

  if (alunos === null) return <p className="text-xs text-gray-500 mt-2">Carregando alunos…</p>;
  if (alunos.length === 0) return <p className="text-xs text-gray-500 mt-2 bg-gray-50 rounded-lg p-2" data-testid="curso-alunos-empty">Nenhum cadastro pelo link deste curso ainda. Compartilhe o link/QR com a turma!</p>;
  return (
    <div className="mt-2 space-y-1.5" data-testid="curso-alunos-list">
      <p className="text-xs text-gray-600">Recebeu a lista oficial dos formados? Marque ✔ em quem concluiu — o selo vira "✅ Formado".</p>
      {alunos.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-2 bg-gray-50 border rounded-lg px-3 py-2 text-sm">
          <div className="min-w-0">
            <p className="font-semibold truncate">{a.confirmed ? "✅" : "🎓"} {a.name}</p>
            <p className="text-xs text-gray-500 truncate">{a.phone || a.email}</p>
          </div>
          <Button size="sm" variant={a.confirmed ? "outline" : "default"}
            className={a.confirmed ? "" : "bg-green-700 hover:bg-green-800"}
            onClick={() => toggle(a.id)} data-testid={`curso-confirm-${a.id}`}>
            {a.confirmed ? "Desfazer" : "✔ Confirmar formado"}
          </Button>
        </div>
      ))}
    </div>
  );
};

export default function CursosAdminPanel() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [shareOpen, setShareOpen] = useState(null);
  const [shareMsg, setShareMsg] = useState("");
  const [alunosOpen, setAlunosOpen] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/cursos`).then((r) => { setCourses(r.data.courses || []); }).catch(() => toast.error("Erro ao carregar cursos")).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (text, msg = "Copiado!") => {
    navigator.clipboard.writeText(text).then(() => toast.success(msg)).catch(() => toast.error("Não foi possível copiar"));
  };

  const downloadQr = async (c) => {
    try {
      const r = await axios.get(`${API}/admin/cursos/${c.id}/qr`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qr-curso-${c.slug}.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("QR baixado! Mande para o responsável imprimir.");
    } catch {
      toast.error("Erro ao gerar QR");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir o curso de ${c.city}? Esta ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/admin/cursos/${c.id}`);
      toast.success("Curso excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const openShare = (c) => {
    setShareOpen(c.id);
    setShareMsg(shareText(c));
  };

  const estadoBadge = (e) => ({
    ativo: "bg-green-100 text-green-700",
    rascunho: "bg-amber-100 text-amber-700",
    encerrado: "bg-gray-100 text-gray-500",
    inativo: "bg-gray-100 text-gray-500",
  }[e] || "bg-gray-100 text-gray-500");

  return (
    <div className="space-y-4" data-testid="cursos-admin-panel">
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="pt-4 pb-4 text-sm">
          <p className="font-semibold text-green-900">🎓 Página pública de cursos</p>
          <p className="text-green-800 break-all mt-1">https://heveatalent.com.br/cursos</p>
          <div className="flex flex-wrap gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => copy("https://heveatalent.com.br/cursos")} data-testid="cursos-copy-public">📋 Copiar</Button>
            <Button size="sm" variant="outline" onClick={() => window.open("/cursos", "_blank")} data-testid="cursos-open-public">Abrir página</Button>
            <Button size="sm" variant="outline" onClick={() => copy("https://heveatalent.com.br/parceiros", "Link de parceiros copiado!")} data-testid="cursos-copy-parceiros">🤝 Copiar link p/ sindicatos (/parceiros)</Button>
          </div>
          <div className="mt-3 border-t border-green-200 pt-3" data-testid="cursos-links-individuais">
            <p className="font-semibold text-green-900 text-xs">🔗 Links individuais (envie direto para cada turma):</p>
            <div className="flex flex-wrap gap-2 mt-2">
              <Button size="sm" variant="outline" onClick={() => copy("https://heveatalent.com.br/curso/ead-sistemas-cultivo", "Link do EAD Cultivo/Látex copiado!")} data-testid="cursos-copy-ead-cultivo">
                💻 EAD Cultivo e Látex
              </Button>
              <Button size="sm" variant="outline" onClick={() => copy("https://heveatalent.com.br/curso/ead-producao-mudas", "Link do EAD Mudas copiado!")} data-testid="cursos-copy-ead-mudas">
                💻 EAD Produção de Mudas
              </Button>
            </div>
            <p className="text-[11px] text-green-700 mt-1.5">Os cursos presenciais têm o botão "🎓 Turma" na lista abaixo.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <CardTitle className="text-base">Cursos cadastrados ({courses.length})</CardTitle>
            {!showForm && !editCourse && (
              <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => setShowForm(true)} data-testid="cursos-new-btn">➕ Novo curso</Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {showForm && (
            <CursoForm initial={EMPTY_FORM} editId={null}
              onSaved={() => { setShowForm(false); load(); }}
              onCancel={() => setShowForm(false)} />
          )}
          {editCourse && (
            <CursoForm initial={{ ...EMPTY_FORM, ...editCourse, vagas: editCourse.vagas ?? "" }} editId={editCourse.id}
              onSaved={() => { setEditCourse(null); load(); }}
              onCancel={() => setEditCourse(null)} />
          )}

          {loading && <p className="text-sm text-gray-500">Carregando…</p>}
          {!loading && courses.length === 0 && !showForm && (
            <p className="text-sm text-gray-500" data-testid="cursos-empty-admin">Nenhum curso cadastrado. Clique em "➕ Novo curso" — o modelo SENAR já vem preenchido.</p>
          )}

          {courses.map((c) => (
            <div key={c.id} className={`border rounded-xl p-3 ${c.estado === "rascunho" ? "border-amber-300 bg-amber-50/40" : ""}`} data-testid={`curso-admin-${c.slug}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-sm">
                    {c.title} — {c.city}{c.uf ? `/${c.uf}` : ""}
                    <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${estadoBadge(c.estado)}`}>{c.estado}</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">📅 {c.dates_label}{c.local ? ` · 📌 ${c.local}` : ""}{c.contact_name ? ` · 👤 ${c.contact_name}` : ""}</p>
                  {c.estado === "rascunho" && <p className="text-xs text-amber-700 mt-1 font-semibold">⚠️ Complete as datas (Editar) para publicar.</p>}
                  <p className="text-xs text-gray-700 mt-1" data-testid={`curso-stats-${c.slug}`}>
                    👁 <b>{c.visits}</b> visita{c.visits !== 1 ? "s" : ""} ({c.visits_qr} via QR) · 📝 <b>{c.signups}</b> cadastro{c.signups !== 1 ? "s" : ""} · ✅ <b>{c.confirmed}</b> formado{c.confirmed !== 1 ? "s" : ""} confirmado{c.confirmed !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex flex-wrap gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openShare(c)} data-testid={`curso-share-${c.slug}`}>📣 Divulgar</Button>
                  <Button size="sm" variant="outline" onClick={() => downloadQr(c)} data-testid={`curso-qr-${c.slug}`}>🔳 QR</Button>
                  <Button size="sm" variant="outline" className="bg-green-50 border-green-300 text-green-800" onClick={() => copy(`https://heveatalent.com.br/curso/${c.slug}`, "Link da turma copiado! Envie no grupo dos alunos.")} data-testid={`curso-turma-link-${c.slug}`}>🎓 Turma</Button>
                  <Button size="sm" variant="outline" onClick={() => copy(c.tracked_link, "Link rastreável copiado!")} data-testid={`curso-link-${c.slug}`}>🔗 Link</Button>
                  <Button size="sm" variant="outline" onClick={() => setAlunosOpen(alunosOpen === c.id ? null : c.id)} data-testid={`curso-alunos-btn-${c.slug}`}>👥 Alunos ({c.signups})</Button>
                  <Button size="sm" variant="outline" onClick={() => { setEditCourse(c); setShowForm(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`curso-edit-${c.slug}`}>✏️</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(c)} data-testid={`curso-delete-${c.slug}`}>🗑</Button>
                </div>
              </div>

              {shareOpen === c.id && (
                <div className="mt-3 border-t pt-3" data-testid={`curso-share-box-${c.slug}`}>
                  <p className="text-xs font-semibold text-gray-600 mb-1">Texto para os grupos (edite à vontade antes de enviar):</p>
                  <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows={7} value={shareMsg}
                    onChange={(e) => setShareMsg(e.target.value)} data-testid={`curso-share-text-${c.slug}`} />
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button size="sm" className="bg-green-700 hover:bg-green-800" onClick={() => copy(shareMsg, "Texto copiado! Cole nos grupos.")} data-testid={`curso-share-copy-${c.slug}`}>📋 Copiar texto</Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareMsg)}`, "_blank")} data-testid={`curso-share-wa-${c.slug}`}>Abrir WhatsApp</Button>
                    <Button size="sm" variant="ghost" onClick={() => setShareOpen(null)}>Fechar</Button>
                  </div>
                </div>
              )}

              {alunosOpen === c.id && <AlunosList courseId={c.id} />}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
